import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import type { PersonalChartAlerts as AlertState } from '@/hooks/useChartAlerts'
import {
  CHART_ALERT_DELIVERIES, chartAlertDelivery, chartAlertNotification, formatChartAlertLevel,
  type ChartAlertDelivery, type ChartLevelAlert,
} from '@/lib/chartAlerts'

const selectClass = 'h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs text-slate-100 disabled:opacity-50'

function DeliverySelect({ value, onChange, disabled, label }: {
  value: ChartAlertDelivery; onChange: (value: ChartAlertDelivery) => void; disabled?: boolean; label: string
}) {
  return <select aria-label={label} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value as ChartAlertDelivery)} className={selectClass}>
    {CHART_ALERT_DELIVERIES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
  </select>
}

function AlertRow({ alert, state, onSelectSymbol }: {
  alert: ChartLevelAlert; state: AlertState; onSelectSymbol: (symbol: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [threshold, setThreshold] = useState(String(alert.threshold))
  const [delivery, setDelivery] = useState(chartAlertDelivery(alert))
  const [error, setError] = useState('')
  const busy = state.savingId === alert.id
  const active = alert.status === 'active'
  const route = chartAlertDelivery(alert)
  const availability = state.availability?.[alert.notification_channel]
  const deliveryName = CHART_ALERT_DELIVERIES.find((option) => option.value === route)?.label

  async function save() {
    const level = Number(threshold)
    if (!Number.isFinite(level) || level <= 0) { setError('Enter a level greater than zero.'); return }
    setError('')
    try {
      await state.update(alert.id, { ...(level !== alert.threshold ? { threshold: level } : {}), ...chartAlertNotification(delivery) })
      setEditing(false)
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save alert.') }
  }

  async function cancel() {
    setError('')
    try { await state.cancel(alert.id) }
    catch (err) { setError(err instanceof Error ? err.message : 'Could not cancel alert.') }
  }

  return <li className="rounded-lg border border-slate-800 bg-slate-950/50 p-3" data-alert-id={alert.id}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <button type="button" className="font-semibold text-slate-100 underline-offset-4 hover:underline" onClick={() => onSelectSymbol(alert.symbol)}>{alert.symbol}</button>
          <span className="text-[10px] uppercase tracking-wide text-slate-400">{alert.exchange} · {alert.metric === 'price' ? `${alert.market || 'futures'} price` : 'OI'}</span>
        </div>
        <div className="mt-1 font-mono text-sm text-amber-300">{alert.direction === 'above' ? '≥' : '≤'} {formatChartAlertLevel(alert)}</div>
      </div>
      <div className="flex items-center gap-2">
        {alert.status === 'triggered' && <Button asChild variant="outline" size="sm" className="h-8"><Link to={`/alert-reviews?symbol=${encodeURIComponent(alert.symbol)}`}>Review</Link></Button>}
        <div className="text-right text-xs">
          <div className={active ? 'text-emerald-400' : 'text-slate-400'}>{active ? 'Enabled' : alert.status === 'triggered' ? 'Triggered' : 'Cancelled'}</div>
          <div className="mt-1 text-slate-400">{deliveryName}</div>
        </div>
        {active && <>
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={state.isSaving} aria-label={`Edit alert ${alert.id}`} onClick={() => {
            setThreshold(String(alert.threshold)); setDelivery(route); setError(''); setEditing(!editing)
          }}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-rose-400" disabled={state.isSaving} aria-label={`Cancel alert ${alert.id}`} onClick={() => void cancel()}><Trash2 className="h-3.5 w-3.5" /></Button>
        </>}
      </div>
    </div>
    {active && availability && !availability.ready && <p className="mt-2 text-xs text-amber-300">{availability.error} Update your <Link className="underline" to="/account">account</Link> or change delivery.</p>}
    {alert.status === 'triggered' && <p className="mt-2 text-[11px] text-slate-500">
      {alert.triggered_at ? new Date(alert.triggered_at * 1000).toLocaleString() : 'Level reached'}
      {' · '}{alert.delivery_status === 'sent' ? 'Notification sent' : 'Notification pending; delivery will retry'}
    </p>}
    {alert.delivery_error && <p className="mt-2 text-xs text-amber-300">{alert.delivery_error} <Link to="/account" className="underline">Account settings</Link></p>}
    {editing && active && <form onSubmit={(event) => { event.preventDefault(); void save() }} className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-800 pt-3">
      <label className="grid gap-1 text-[11px] text-slate-400">{alert.metric === 'price' ? 'Price (USDT)' : 'OI (coins)'}
        <input aria-label={`Alert ${alert.id} threshold`} type="number" step="any" min="0" required value={threshold} disabled={busy} onChange={(event) => setThreshold(event.target.value)} className={`${selectClass} w-40 font-mono`} />
      </label>
      <label className="grid gap-1 text-[11px] text-slate-400">Notification
        <DeliverySelect value={delivery} onChange={setDelivery} disabled={busy} label={`Alert ${alert.id} notification`} />
      </label>
      <Button type="submit" size="sm" className="h-8" disabled={state.isSaving}>{busy ? 'Saving…' : 'Save'}</Button>
      <Button type="button" variant="ghost" size="sm" className="h-8" disabled={busy} onClick={() => setEditing(false)}>Close</Button>
      {delivery === 'emergency' && <p className="w-full text-[11px] text-amber-300">Repeats every minute until acknowledged, for up to one hour.</p>}
      <p className="w-full text-[11px] text-slate-500">Changing the level resets the crossing direction using the current market value.</p>
    </form>}
    {error && <p role="alert" className="mt-2 text-xs text-rose-400">{error}</p>}
  </li>
}

export function PersonalChartAlerts({ state, onSelectSymbol }: { state: AlertState; onSelectSymbol: (symbol: string) => void }) {
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState(false)
  const active = state.alerts.filter((alert) => alert.status === 'active')
  const visible = history ? state.alerts.filter((alert) => alert.status !== 'active') : active
  const selectedAvailability = state.availability?.[state.delivery === 'telegram' ? 'telegram' : 'pushover']
  return <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (next) state.refresh() }}>
    <DialogTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2 text-[10px]" title="Your personal price and OI alerts">
        <Bell className="h-3.5 w-3.5 text-amber-400" /> My alerts
        <span className="rounded bg-amber-400/10 px-1.5 font-mono text-amber-300">{active.length}</span>
      </Button>
    </DialogTrigger>
    <DialogContent className="flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-4 border-slate-800 bg-[#090f19] text-slate-100">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-amber-400" />My chart alerts</DialogTitle>
        <DialogDescription>Private to your account, across all symbols. Each alert triggers once when a fresh price or OI sample reaches its level.</DialogDescription>
      </DialogHeader>
      <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-slate-300">Delivery for new double-click alerts</span>
          <DeliverySelect value={state.delivery} onChange={state.setDelivery} label="New chart alert notification" />
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Double-click a price or OI graph at your target level. Right-click a yellow alert line to delete it. Drag a line to change its value; release to save or press Escape or right-click during the drag to cancel the move. In merged view, use the nearest exchange line. Alerts keep running when you close the page.</p>
        {state.delivery === 'emergency' && <p className="mt-2 text-[11px] text-amber-300">Emergency Pushover repeats every minute until acknowledged, for up to one hour.</p>}
        {selectedAvailability && !selectedAvailability.ready && <p role="status" className="mt-2 text-xs text-amber-300">{selectedAvailability.error} <Link to="/account" className="underline">Open account settings</Link></p>}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant={history ? 'ghost' : 'secondary'} onClick={() => setHistory(false)}>Enabled ({active.length})</Button>
        <Button size="sm" variant={history ? 'secondary' : 'ghost'} onClick={() => setHistory(true)}>History</Button>
        <Link className="ml-auto text-xs text-slate-400 hover:text-slate-100" to="/account">Notification connections</Link>
      </div>
      {state.error && <p role="alert" className="text-xs text-rose-400">{state.error instanceof Error ? state.error.message : 'Could not load your alerts.'} <button className="underline" type="button" onClick={state.refresh}>Retry</button></p>}
      <ul className="min-h-20 space-y-2 overflow-y-auto pr-1">
        {state.isLoading ? <li className="py-6 text-center text-sm text-slate-500">Loading your alerts…</li>
          : visible.length === 0 ? <li className="py-6 text-center text-sm text-slate-500">{history ? 'No past alerts yet.' : 'No enabled alerts. Double-click a chart to add one.'}</li>
          : visible.map((alert) => <AlertRow key={alert.id} alert={alert} state={state} onSelectSymbol={(symbol) => { onSelectSymbol(symbol); setOpen(false) }} />)}
      </ul>
    </DialogContent>
  </Dialog>
}
